import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { runProgress } from '../../services/workbook/estimate';
import type { IndexState } from '../../services/workbook/WorkbookIndexer';
import { IndexingAnimation } from './IndexingAnimation';
import { ProgressBar } from './ProgressBar';

/** The first index, while it runs: a picture of the agent at work on the sheet, then what it is doing now, how
 *  far it has got, and how long it has been.
 *
 *  Only the FIRST one takes the pane. Every re-index after it runs behind the chat, with its progress in
 *  the status line under the header. */
export function IndexingView({ index, onRetry }: { index: IndexState; onRetry: () => void }) {
  const { t } = useTranslation();
  // One bar for the whole run. Each phase used to fill its own, so the user watched it finish and start
  // again; `runProgress` banks the reading's share before the model pass adds to it.
  const { fraction, done, total } = runProgress(index, index.size);
  const elapsed = useElapsed(index.startedAt);

  if (index.phase === 'failed') {
    return (
      <div className="flex flex-1 flex-col justify-center px-5 py-6" data-testid="indexing-failed">
        <div className="mx-auto flex w-full max-w-[320px] flex-col gap-3">
          <p className="text-[13px] text-danger">{t('index.failed', { error: index.error ?? '' })}</p>
          <button type="button" onClick={onRetry} className="w-full rounded-[6px] bg-accent py-2.5 text-[13px] font-medium text-white hover:bg-accent-hover">
            {t('intro.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col justify-center px-5 py-6" data-testid="indexing-view" role="status" aria-live="polite">
      <div className="mx-auto flex w-full max-w-[320px] flex-col gap-3">
        <div className="mb-2">
          <IndexingAnimation phase={index.phase} />
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[13px] font-medium text-ink">{t(`intro.phase.${index.phase}`)}</span>
          {elapsed && <span className="shrink-0 font-mono text-[11px] text-fainter">{elapsed}</span>}
        </div>

        <ProgressBar fraction={fraction} />

        <p className="text-[12px] text-muted">
          {total > 0 ? t('intro.progress', { done, total }) : t('intro.starting')}
          {index.current && <span className="text-fainter"> · {index.current}</span>}
        </p>

        <p className="text-[11.5px] leading-[1.45] text-fainter">{t('intro.patience')}</p>
      </div>
    </div>
  );
}

/** Seconds since the run started, ticking. Shown from the first second here — unlike the chat's activity
 *  line, a wait the user was told about is a wait they are entitled to watch. */
function useElapsed(since: number | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!since) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [since]);
  if (!since) return null;
  const s = Math.max(0, Math.floor((now - since) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
